// Lib
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Utils
import { WebCrashReportPlugin } from '../../../capacitor_plugins/webCrashReport';
import { canvasZoomScaleSelector } from '../../canvas/store/canvasZoomSelector';
import zoomState from '../../canvas/zoom/zoomStateSingleton';
import { getCurrentBoardVisibleDescendantIds } from '../../element/selectors/currentBoardSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';

// Types
import { CAPACITOR_WEB_CRASH_REPORT_PLUGIN } from '../../../capacitor_plugins/pluginConstants';

export default function useCrashReporting() {
    const isPluginAvailable = useMemo(() => Capacitor.isPluginAvailable(CAPACITOR_WEB_CRASH_REPORT_PLUGIN), []);

    // Keep some crash-relevant data in local storage so that we can read it after a crash.
    const boardId = useSelector(getCurrentBoardId);
    const elementCount = useSelector(getCurrentBoardVisibleDescendantIds)?.length;
    const zoomLevel = useSelector(canvasZoomScaleSelector);
    useEffect(() => {
        if (!isPluginAvailable) return;
        WebCrashReportPlugin.recordBoardData({ boardId, elementCount, zoomLevel });
    }, [boardId, elementCount, zoomLevel]);

    // Keep the zoom level updated whenever it changes (this is at a higher freq than redux's updates)
    useEffect(() => {
        if (!isPluginAvailable) return;
        const onZoomChange = (zoom: number) => {
            WebCrashReportPlugin.recordZoomLevel({ zoomLevel: zoom });
        };

        zoomState.registerZoomSubscriber(onZoomChange);

        return () => {
            zoomState.unregisterZoomSubscriber(onZoomChange);
        };
    }, []);
}
